// IMPORTANT Autogenerated file
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  AssetOrAccessoryRequestDto,
  AssetOrAccessoryRequestFlatDto,
  PARTICIPANT_FLAT_KEYS,
  RequestWidgetDto,
  flatToParticipants,
  participantsToFlat,
} from '@ellf/common-dto';
import {
  DownloadExcelType,
  FieldConfig,
  FilterValue,
  PagedResultDto,
} from '@ellf/common-interfaces';
import { Update } from '@ngrx/entity';
import { map, Observable } from 'rxjs';
import { ApiClientConfig } from '../api-client-config';
import { BaseCRUDApiClient } from './baseCRUD.api-client';
import {
  AssetOrAccessoriesViewEnum,
  FilterCustomPermissionsEnum,
} from '@ellf/common-utils';

@Injectable()
// Il backend espone i soggetti coinvolti come participants[] (una riga per ruolo); il frontend lavora
// con i campi piatti (un gruppo per ruolo, come nel form). La conversione sta solo qui.
export class AssetOrAccessoryRequestApiClient extends BaseCRUDApiClient<AssetOrAccessoryRequestFlatDto> {
  baseUrl = this.config.apiEndpoint + '/asset-or-accessory-request';

  constructor(http: HttpClient, config: ApiClientConfig) {
    super(http, config);
  }

  private fromDto(
    dto: AssetOrAccessoryRequestDto,
  ): AssetOrAccessoryRequestFlatDto {
    const { participants, ...request } = dto;
    return { ...request, ...participantsToFlat(participants) };
  }

  private fromPagedDto(
    result: PagedResultDto<AssetOrAccessoryRequestDto>,
  ): PagedResultDto<AssetOrAccessoryRequestFlatDto> {
    return { ...result, items: result.items.map((i) => this.fromDto(i)) };
  }

  private toDto(
    flat: Partial<AssetOrAccessoryRequestFlatDto>,
  ): Partial<AssetOrAccessoryRequestDto> {
    const request: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(flat)) {
      if (!PARTICIPANT_FLAT_KEYS.includes(key)) request[key] = value;
    }
    const participants = flatToParticipants(flat);
    return participants.length > 0 ? { ...request, participants } : request;
  }

  override getAll() {
    return this.http
      .get<AssetOrAccessoryRequestDto[]>(this.baseUrl)
      .pipe(map((items) => items.map((i) => this.fromDto(i))));
  }

  override getPaged(
    take: number,
    skip: number,
    orderBy?: { [field: string]: 'asc' | 'desc' },
    filters?: FilterValue[],
    join?: string[],
    skipFilterPermission?: FilterCustomPermissionsEnum[],
  ) {
    const mappedFilters = this.mapFilters(filters);

    return this.http
      .post<PagedResultDto<AssetOrAccessoryRequestDto>>(
        `${this.baseUrl}/paged`,
        {
          skip,
          take,
          orderBy,
          filters: mappedFilters,
          join,
          skipFilterPermission,
        },
      )
      .pipe(map((result) => this.fromPagedDto(result)));
  }

  override getById(id: string) {
    return this.http
      .get<AssetOrAccessoryRequestDto>(`${this.baseUrl}/${id}`)
      .pipe(map((dto) => this.fromDto(dto)));
  }

  override add(u: Partial<AssetOrAccessoryRequestFlatDto>) {
    return this.http
      .post<AssetOrAccessoryRequestDto>(this.baseUrl, this.toDto(u))
      .pipe(map((dto) => this.fromDto(dto)));
  }

  override update(u: Update<AssetOrAccessoryRequestFlatDto>) {
    return this.http
      .put<AssetOrAccessoryRequestDto>(
        `${this.baseUrl}/${u.id}`,
        this.toDto(u.changes),
      )
      .pipe(map((dto) => this.fromDto(dto)));
  }

  createDocument(id: string) {
    return this.http
      .post(
        `${this.baseUrl}/create-document`,
        { id: id },
        {
          responseType: 'arraybuffer',
        },
      )
      .pipe(
        map((file: ArrayBuffer) => {
          return file;
        }),
      );
  }

  saveAndAttachDocument(id: string) {
    return this.http.post(`${this.baseUrl}/save-and-attach-document`, {
      id: id,
    });
  }

  getPagedWithViewFlag(
    viewMode: AssetOrAccessoriesViewEnum,
    take: number,
    skip: number,
    orderBy?: { [field: string]: 'asc' | 'desc' },
    filters?: FilterValue[],
    join?: string[],
  ) {
    const mappedFilters = this.mapFilters(filters);

    return this.http
      .post<PagedResultDto<AssetOrAccessoryRequestDto>>(
        `${this.baseUrl}/pagedview/${viewMode}`,
        {
          skip,
          take,
          orderBy,
          filters: mappedFilters,
          join,
        },
      )
      .pipe(map((result) => this.fromPagedDto(result)));
  }

  createRequiredEquipmentExcel(id: string): Observable<ArrayBuffer> {
    return this.http
      .post(
        `${this.baseUrl}/create-required-equipment-excel`,
        { id: id },
        {
          responseType: 'arraybuffer',
        },
      )
      .pipe(
        map((file: ArrayBuffer) => {
          return file;
        }),
      );
  }

  override downloadExcel(
    fileName?: string | undefined,
    columns?: FieldConfig[] | undefined,
    filters?: FilterValue[] | undefined,
    orderBy?: { [field: string]: 'asc' | 'desc' } | undefined,
    join?: string[] | undefined,
    downloadExcelType?: any, //DownloadExcelType
  ): Observable<ArrayBuffer> {
    return super.downloadExcel(
      fileName,
      columns,
      filters,
      orderBy,
      join,
      downloadExcelType === DownloadExcelType.accessori
        ? '/download-accessori-excel'
        : '/download-excel',
    );
  }

  getRequestInPeriod(fromDate: string | undefined, toDate: string | undefined) {
    return this.http.post<RequestWidgetDto>(
      `${this.baseUrl}/get-request-in-period`,
      {
        fromDate,
        toDate,
      },
    );
  }
}
